/**
 * Supabase query functions for live results page
 * These functions fetch and transform data from the database
 */

import { supabase } from './supabase';
import type {
  Race,
  AidStation,
  RunnerSummary,
  RunnerDetail,
  SplitTime,
  RunnerStatus,
} from './types';
import type { SimulationState } from '@as-checkin/shared';
import {
  calculateRunnerStatus,
  getStatusCssClass,
  type StatusEvent,
  type StatusStation,
} from './runnerStatus';
import { localToUTC } from './timeFormatter';
import { mapRaceStatus, normalizeRaceRow } from './raceMappers';
import { isHiddenParentStation } from './hiddenParentStation';

// Re-export the pure status mapper so callers/tests can reach it via the
// queries module. See raceMappers.ts for why the implementation lives in its
// own (Supabase-free) module.
export { mapRaceStatus } from './raceMappers';

/**
 * Canonical "event time" — when the runner was actually scanned in the field.
 *
 * Every check-in carries `device_timestamp` (set on the volunteer's device at the
 * moment of the scan, preserved through sync — the truth) and `server_timestamp`
 * (`DEFAULT now()`). When a device is offline and later dumps its queued scans,
 * every row in that batch gets a `server_timestamp` at the sync moment — hours
 * after the scan — which corrupts finish times, splits, pace, last-seen, and
 * placement. Public results must read the real scan time. See AD-228.
 *
 * NOTE: `race_start` events are system-issued (device == server), so the elapsed
 * baselines below intentionally keep reading `server_timestamp`.
 */
function scanTime(
  e: { device_timestamp?: string | null; server_timestamp?: string | null } | null | undefined
): string | null {
  if (!e) return null;
  return e.device_timestamp || e.server_timestamp || null;
}

/**
 * MERGE NOTE — schema remap (dictionary §3.12).
 * This module was re-vendored from as-checkin HEAD, which queried the OLD
 * as-checkin tables. Every query below now targets the MERGED Race Command
 * schema and its anon-facing views:
 *   - `races`        → `race_events`  (table RENAME; `date_start`→`date`,
 *                                      `planning`→`draft` via mapRaceStatus)
 *   - `aid_stations` → `live_stations` VIEW (anon-facing; publish predicate
 *                      embedded; columns pre-aliased to the wire names)
 *   - `runners`      → `live_roster`  VIEW (anon-facing; publish predicate
 *                      embedded; `id`=people.id, real dns/is_anonymous/
 *                      early_start columns)
 *   - `events`       → `checkin_events` (table RENAME; `runner_id`=people.id)
 * The client-facing types (Race, AidStation, RunnerSummary, …) are unchanged —
 * only the source tables/columns change. Anon-key-only, read-only, no RPCs.
 */

/**
 * Convert a raw `live_roster` view row into the client-facing runner shape used
 * throughout this module.
 *
 * WHY a view (not the base tables): in the merged schema a runner is split
 * across `runner_records` (registration) + `people` (identity). `live_roster`
 * is the anon-facing view that re-joins them with the publish predicate
 * (`live_results_public AND NOT is_practice`) embedded, and exposes the REAL
 * `dns` (= runner_records.did_not_start), `is_anonymous`, and `early_start`
 * columns — the March remap wrongly hardcoded those. The view's `id` equals
 * `people.id`, which is exactly what `checkin_events.runner_id` echoes, so
 * runner↔event matching Just Works with no id translation.
 */
function mapRosterRow(r: {
  id: string;
  bib: string;
  first_name: string | null;
  last_name: string | null;
  is_anonymous: boolean | null;
  early_start: boolean | null;
  dns: boolean | null;
  gender: string | null;
}) {
  return {
    id: r.id, // = people.id (matches checkin_events.runner_id)
    bib: r.bib,
    first_name: r.first_name ?? '',
    last_name: r.last_name ?? '',
    is_anonymous: r.is_anonymous ?? false,
    early_start: r.early_start ?? false,
    dns: r.dns ?? false,
    gender: r.gender ?? null,
  };
}

// Column list for the anon roster view. `live_roster` already aliases the
// merged columns back to these wire names, so no per-column remapping is needed.
const ROSTER_COLUMNS =
  'id, bib, first_name, last_name, is_anonymous, early_start, dns, gender';

/**
 * Fetch the actual race start timestamp (earliest race_start event)
 * Returns null if race hasn't been started yet
 */
export async function fetchActualRaceStart(raceId: string): Promise<string | null> {
  // events → checkin_events (dictionary §3.3). `race_id` keeps its name on
  // checkin_events (the FK-suffix asymmetry: it does NOT become event_id).
  const { data, error } = await supabase
    .from('checkin_events')
    .select('server_timestamp')
    .eq('race_id', raceId)
    .eq('event_type', 'race_start')
    .order('server_timestamp', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching actual race start:', error);
    return null;
  }

  return data?.server_timestamp || null;
}

/**
 * Fetch a race by slug.
 *
 * There used to be a second `fetchRaceBySlugWithElevation` variant because the
 * elevation TSV was ~2 MB and V1 pages had no use for it. The stored profile is
 * now ~8 KB of derived JSON, so there is nothing left to avoid downloading and
 * one query serves every page.
 */
export async function fetchRaceBySlug(slug: string): Promise<Race | null> {
  // races → race_events. Select list is the public-safe whitelist (dictionary
  // §6.1) — note `date_start` (renamed from `date`) and that phones/pairing_code
  // are deliberately absent (they moved to the Express-only race_event_secrets
  // table and are NOT anon-readable). normalizeRaceRow maps date_start→date and
  // planning→draft so no raw DB value reaches render.
  const { data, error } = await supabase
    .from('race_events')
    .select('id, name, slug, date_start, start_time, distance, location, status, test_mode, time_zone, elevation_profile, youtube_livestream_url, spot_tracker_url')
    .eq('slug', slug)
    .maybeSingle();

  if (error) {
    console.error('Error fetching race by slug:', error);
    throw new Error('Failed to load race information. Please try again later.');
  }

  return data ? normalizeRaceRow(data) : null;
}

/**
 * Fetch all aid stations for a race, ordered by position
 * @throws Error with user-friendly message if fetch fails
 */
export async function fetchAidStations(raceId: string): Promise<AidStation[]> {
  // aid_stations → live_stations VIEW (dictionary §3.12 / §5). The anon-facing
  // view already aliases the merged base columns back to the wire names
  // (event_id→race_id, mile_marker→mileage, sort_order→position,
  // runner_cutoff→cutoff_time) AND embeds the publish predicate, so the select
  // list and the AidStation shape line up 1:1 with no manual remapping.
  const { data, error } = await supabase
    .from('live_stations')
    .select('id, race_id, name, mileage, position, cutoff_time, expected_visits, parent_station_id, check_mode')
    .eq('race_id', raceId)
    .order('position', { ascending: true });

  if (error) {
    console.error('Error fetching aid stations:', error);
    throw new Error('Failed to load aid station data. Please refresh the page.');
  }

  return (data as AidStation[]) || [];
}

/**
 * NOTE: The old determineRunnerStatus function has been replaced by the
 * calculateRunnerStatus utility in runnerStatus.ts. This provides richer
 * status labels (e.g., "Enroute to Bear Creek") that match the volunteer app.
 * 
 * The new utility is imported at the top of this file and used in
 * fetchRunnersSummary and fetchRunnerDetail.
 */

/**
 * Fetch runners summary with latest check-in info
 * This is the main data for the runner list
 * 
 * NOTE: DNS runners are excluded from the returned array - they are hidden
 * from the public live results page per the design spec.
 */
export async function fetchRunnersSummary(
  raceId: string
): Promise<RunnerSummary[]> {
  // Fetch race info for status utility and fallback start time
  // We need date, start_time, and time_zone for calculating race_start_time fallback
  // when a runner doesn't have an individual race_start event
  // races → race_events. `date` is now `date_start`; status runs through
  // mapRaceStatus (planning→draft). calculateRunnerStatus only understands
  // draft|active|completed, so a `cancelled` race is treated as `completed`
  // for status purposes (a cancelled race is no longer running).
  const { data: race, error: raceError } = await supabase
    .from('race_events')
    .select('status, date_start, start_time, time_zone')
    .eq('id', raceId)
    .maybeSingle();

  if (raceError) {
    console.error('Error fetching race:', raceError);
    throw new Error('Failed to load race information. Please refresh the page.');
  }

  const mappedStatus = mapRaceStatus(race?.status);
  const raceStatus = (mappedStatus === 'cancelled' ? 'completed' : mappedStatus) as
    | 'draft'
    | 'active'
    | 'completed';
  const raceTimeZone = race?.time_zone || 'America/Los_Angeles'; // Default for safety

  // Calculate fallback race start time from race configuration
  // This is used when a runner doesn't have an individual race_start event
  // (matches the fallback logic in fetchRunnerDetail for consistency)
  let fallbackRaceStartTime: string | null = null;
  if (race?.start_time && race?.date_start) {
    // Construct from race date + start_time (in race's local timezone)
    // Convert local time to UTC using raceTimeZone
    const localDateTime = `${race.date_start}T${race.start_time}`;
    fallbackRaceStartTime = localToUTC(localDateTime, raceTimeZone);
  } else if (race?.date_start) {
    // No start time - use race date at midnight (local time, convert to UTC)
    const localDateTime = `${race.date_start}T00:00:00`;
    fallbackRaceStartTime = localToUTC(localDateTime, raceTimeZone);
  }

  // Fetch all runners for the race via the anon-facing live_roster VIEW.
  // runners → live_roster (dictionary §3.12): the view re-joins runner_records +
  // people, embeds the publish predicate, and exposes real dns/is_anonymous/
  // early_start columns. `id` = people.id (matches checkin_events.runner_id).
  // DNS runners are still returned here (dns=true) and filtered out in JS below.
  const { data: rawRunners, error: runnersError } = await supabase
    .from('live_roster')
    .select(ROSTER_COLUMNS)
    .eq('race_id', raceId)
    .order('bib', { ascending: true });

  if (runnersError) {
    console.error('Error fetching runners:', runnersError);
    throw new Error('Failed to load runner list. Please refresh the page.');
  }

  if (!rawRunners || rawRunners.length === 0) {
    return [];
  }

  const runners = rawRunners.map(mapRosterRow);

  // Fetch only the events needed for status calculation (optimized for egress)
  // Exclude cancelled events - cancelled events should not affect status or finish time calculations
  //
  // OPTIMIZATION: Instead of fetching all 50,000 events, fetch only what's needed:
  // - race_start events (1 per runner max)
  // - dnf events (if any)
  // - finish events (if any)
  // - Recent location events (check_in, check_out, pass)
  //
  // This reduces egress from ~6MB to ~240KB per query (97% reduction)
  // Analysis of calculateRunnerStatus() shows it only needs these specific event types
  const eventQueries = [
    // Race start events (one per runner max)
    supabase
      .from('checkin_events')
      .select('id, runner_id, station_id, event_type, server_timestamp, device_timestamp, dnf_reason')
      .eq('race_id', raceId)
      .eq('event_type', 'race_start')
      .is('cancelled_at', null)
      .order('device_timestamp', { ascending: false })
      .range(0, 249), // Max 250 runners

    // DNF events
    supabase
      .from('checkin_events')
      .select('id, runner_id, station_id, event_type, server_timestamp, device_timestamp, dnf_reason')
      .eq('race_id', raceId)
      .eq('event_type', 'dnf')
      .is('cancelled_at', null)
      .order('device_timestamp', { ascending: false })
      .range(0, 249),

    // Finish events
    supabase
      .from('checkin_events')
      .select('id, runner_id, station_id, event_type, server_timestamp, device_timestamp, dnf_reason')
      .eq('race_id', raceId)
      .eq('event_type', 'finish')
      .is('cancelled_at', null)
      .order('device_timestamp', { ascending: false })
      .range(0, 249),

    // Location events (check_in, check_out, pass)
    // CRITICAL: We need the most recent location event for EACH runner to calculate
    // "Enroute to [station]" status correctly. During active races with 200+ runners,
    // slower runners whose last check-in occurred hours ago may have their events
    // excluded if we only fetch the N most recent events globally.
    //
    // SOLUTION: Fetch up to 10,000 most recent location events globally (should cover
    // most races: 250 runners × ~40 events each). For runners missing location events
    // in this set, we fetch their most recent location event separately as a fallback.
    // This ensures status calculation always has the most recent location event per runner.
    supabase
      .from('checkin_events')
      .select('id, runner_id, station_id, event_type, server_timestamp, device_timestamp, dnf_reason')
      .eq('race_id', raceId)
      .in('event_type', ['check_in', 'check_out', 'pass'])
      .is('cancelled_at', null)
      .order('device_timestamp', { ascending: false })
      .range(0, 9999), // 250 runners × ~40 events = 10,000 max (ensures most recent per runner)
  ];

  const eventResults = await Promise.all(eventQueries);
  let events = eventResults.flatMap(result => result.data || []);

  // Check for errors in any of the queries
  const eventsError = eventResults.find(r => r.error)?.error;

  if (eventsError) {
    console.error('Error fetching events:', eventsError);
    throw new Error('Failed to load runner check-in data. Please refresh the page.');
  }

  // CRITICAL FIX: Ensure we have the most recent location event for each runner
  // The query above fetches the 10,000 most recent location events globally, but
  // during active races with 200+ runners, slower runners' most recent events might
  // still be excluded if faster runners have generated many newer events.
  //
  // SOLUTION: For runners that don't have any location events in the fetched set,
  // fetch their most recent location event separately. This ensures status calculation
  // always has the most recent location event per runner.
  const locationEvents = events.filter(e => 
    e.event_type === 'check_in' || 
    e.event_type === 'check_out' || 
    e.event_type === 'pass'
  );
  
  // Identify runners missing location events
  const runnerIds = new Set(runners.map(r => r.id));
  const runnersWithLocationEvents = new Set(
    locationEvents.map(e => e.runner_id).filter(Boolean) as string[]
  );
  const runnersMissingLocationEvents = Array.from(runnerIds).filter(
    id => !runnersWithLocationEvents.has(id)
  );

  // For runners missing location events, fetch their most recent location event
  // This is a fallback to ensure status calculation works correctly
  if (runnersMissingLocationEvents.length > 0) {
    const missingRunnerQueries = runnersMissingLocationEvents.map(runnerId =>
      supabase
        .from('checkin_events')
        .select('id, runner_id, station_id, event_type, server_timestamp, device_timestamp, dnf_reason')
        .eq('race_id', raceId)
        .eq('runner_id', runnerId)
        .in('event_type', ['check_in', 'check_out', 'pass'])
        .is('cancelled_at', null)
        .order('device_timestamp', { ascending: false })
        .limit(1) // Only need the most recent one
    );

    const missingRunnerResults = await Promise.all(missingRunnerQueries);
    const missingRunnerEvents = missingRunnerResults
      .flatMap(result => result.data || [])
      .filter(Boolean);

    // Add the missing events to our events array
    if (missingRunnerEvents.length > 0) {
      events = [...events, ...missingRunnerEvents];
      console.log(
        `[RunnerList] Fetched ${missingRunnerEvents.length} missing location events ` +
        `for runners without events in main query`
      );
    }
  }

  // Fetch aid stations with fields needed for status calculation
  // (expected_visits and check_mode for filtering parent/inherent stations)
  // aid_stations → live_stations VIEW (anon-facing; publish predicate embedded;
  // columns pre-aliased to wire names). Only the fields the status utility needs.
  const { data: stations, error: stationsError } = await supabase
    .from('live_stations')
    .select('id, name, position, expected_visits, check_mode')
    .eq('race_id', raceId);

  if (stationsError) {
    console.error('Error fetching stations:', stationsError);
    throw new Error('Failed to load aid station information. Please refresh the page.');
  }

  // Convert stations to StatusStation format for the status utility
  const statusStations: StatusStation[] = (stations || []).map(s => ({
    id: s.id,
    name: s.name,
    position: s.position,
    expected_visits: s.expected_visits,
    check_mode: s.check_mode,
  }));

  // Create a map of station ID to station info for quick lookup
  const stationMap = new Map(
    stations?.map((s) => [s.id, { name: s.name, position: s.position }]) || []
  );

  // DEBUG: Log total events fetched for ALL runners (checking if query returned data)
  const locationEventCount = events.filter(e => 
    e.event_type === 'check_in' || e.event_type === 'check_out' || e.event_type === 'pass'
  ).length;
  console.log('[RunnerList] Total events fetched from DB (optimized):', events?.length || 0);
  console.log('[RunnerList] Location events count:', locationEventCount);

  // Warn if we're approaching the location event limit (could cause truncation issues)
  // With 10,000 location events, we should cover most races, but very active races
  // with 200+ runners might need more
  if (locationEventCount >= 9500) {
    console.warn(
      '[RunnerList] ⚠️  Approaching location event limit (10,000)! ' +
      'Some slower runners may have missing location events. Consider increasing range.'
    );
  }

  if (events && events.length > 0) {
    console.log('[RunnerList] First event sample:', events[0]);
    console.log('[RunnerList] Event runner_id type:', typeof events[0]?.runner_id);
  }

  // Process each runner to build summary
  const allSummaries: RunnerSummary[] = runners.map((runner) => {
    // DEBUG: Log details for Bib 95 BEFORE filtering to check UUID match
    if (runner.bib === '95') {
      console.log('[RunnerList] Bib 95 runner.id:', runner.id);
      console.log('[RunnerList] Bib 95 runner.id type:', typeof runner.id);
      console.log('[RunnerList] Total events in array before filter:', events?.length || 0);

      // Check if ANY event has a matching runner_id
      const matchingEvents = events?.filter((e) => e.runner_id === runner.id) || [];
      console.log('[RunnerList] Events matching runner.id:', matchingEvents.length);

      if (matchingEvents.length === 0 && events && events.length > 0) {
        // No matches - let's check a few events to see their runner_ids
        console.log('[RunnerList] Sample of runner_ids in events (first 5):',
          events.slice(0, 5).map(e => ({ runner_id: e.runner_id, type: typeof e.runner_id })));
      }
    }

    // Get all events for this runner
    const runnerEvents: StatusEvent[] =
      (events?.filter((e) => e.runner_id === runner.id) || []).map(e => ({
        event_type: e.event_type,
        station_id: e.station_id,
        server_timestamp: e.server_timestamp,
        device_timestamp: e.device_timestamp,
      }));

    // Calculate status using the shared utility (matches volunteer app)
    const statusResult = calculateRunnerStatus({
      isDns: runner.dns || false,
      events: runnerEvents,
      raceStatus,
      stations: statusStations,
    });

    // DEBUG: Log events for Bib #95 to diagnose status issue
    if (runner.bib === '95') {
      console.log('[RunnerList] Bib 95 events passed to calculateRunnerStatus:', {
        runner_id: runner.id,
        count: runnerEvents.length,
        events: runnerEvents,
        hasRaceStart: runnerEvents.some(e => e.event_type === 'race_start'),
        statusResult,
        raceStatus,
      });
    }

    // Map status code to CSS class name
    const status = getStatusCssClass(statusResult.statusCode) as RunnerStatus;

    // Get latest check-in event for last seen info
    const latestCheckIn = (events || [])
      .filter((e) => e.runner_id === runner.id && e.event_type === 'check_in')
      .sort((a, b) => new Date(scanTime(b)!).getTime() - new Date(scanTime(a)!).getTime())[0];

    // Get finish event timestamp for finished runners
    // Note: Events are sorted descending (newest first), so .find() returns the most recent finish event
    // This is important if a finish was cancelled and the runner finished again
    const finishEvent = (events || [])
      .find((e) => e.runner_id === runner.id && e.event_type === 'finish');

    // Get station name and position for last check-in
    // For DNS runners, don't show last check-in (events are hidden from public)
    const lastStationName = runner.dns
      ? null
      : latestCheckIn
        ? stationMap.get(latestCheckIn.station_id)?.name || 'Unknown'
        : null;

    const lastStationPosition = runner.dns
      ? null
      : latestCheckIn
        ? stationMap.get(latestCheckIn.station_id)?.position ?? null
        : null;

    // Get runner's race_start event timestamp (individual start time)
    // If no individual race_start event exists, fall back to race's scheduled start time
    // This ensures the leaderboard can display elapsed finish times even when
    // runners don't have individual race_start events (edge cases, data migration, etc.)
    const raceStartEvent = (events || [])
      .find((e) => e.runner_id === runner.id && e.event_type === 'race_start');

    const finalRaceStartTime = raceStartEvent?.server_timestamp || fallbackRaceStartTime;

    return {
      id: runner.id,
      bib: runner.bib,
      first_name: runner.first_name,
      last_name: runner.last_name,
      is_anonymous: runner.is_anonymous,
      early_start: runner.early_start,
      dns: runner.dns || false,
      gender: runner.gender || null,
      status,
      status_label: statusResult.statusLabel,
      status_short_label: statusResult.statusShortLabel,
      is_dropped: statusResult.isDropped,
      last_checkin_station: lastStationName,
      last_checkin_station_position: lastStationPosition,
      last_checkin_time: runner.dns ? null : scanTime(latestCheckIn),
      finish_time: runner.dns ? null : scanTime(finishEvent),
      overall_place: null, // Will be calculated below
      gender_place: null, // Will be calculated below
      race_start_time: finalRaceStartTime,
      next_checkpoint: statusResult.nextStation || null,
      next_checkpoint_id: statusResult.nextStationId || null,
      phase:
        statusResult.displayStatus === 'at_station'
          ? 'at_station'
          : statusResult.displayStatus === 'enroute'
            ? 'enroute'
            : null,
    };
  });

  // Calculate placement for all runners with check-ins or finishes
  // Use finish_time for finished runners, last_checkin_time for on-course runners
  const runnersWithProgress = allSummaries
    .filter((r) => r.finish_time || r.last_checkin_time)
    .map((r) => ({
      ...r,
      place_timestamp: r.finish_time || r.last_checkin_time,
      // For finished runners, assign Infinity position so they sort to the top
      // For on-course runners, use their last check-in station position
      place_station_position: r.finish_time
        ? Infinity
        : (r.last_checkin_station_position ?? -1),
    }))
    .sort((a, b) => {
      // Primary sort: Station position descending (furthest = best place)
      // Finished runners have Infinity position, so they always rank first
      const posA = a.place_station_position;
      const posB = b.place_station_position;

      if (posA !== posB) {
        return posB - posA; // Higher position = better place (descending)
      }

      // Secondary sort: Timestamp ascending (earliest = best) for ties at same station
      const timeA = new Date(a.place_timestamp!).getTime();
      const timeB = new Date(b.place_timestamp!).getTime();
      return timeA - timeB;
    });

  // Assign overall place (1-based index)
  // Find the original runner in allSummaries and update it
  runnersWithProgress.forEach((runner, index) => {
    const originalRunner = allSummaries.find((r) => r.id === runner.id);
    if (originalRunner) {
      originalRunner.overall_place = index + 1;
    }
  });

  // Calculate gender-specific placement
  const byGender = new Map<string, typeof runnersWithProgress>();
  for (const runner of runnersWithProgress) {
    // Skip runners without gender - they don't get gender placement
    if (!runner.gender) continue;

    const gender = runner.gender;
    if (!byGender.has(gender)) {
      byGender.set(gender, []);
    }
    byGender.get(gender)!.push(runner);
  }

  // Assign gender place within each gender group
  // Find the original runner in allSummaries and update it
  byGender.forEach((genderGroup) => {
    genderGroup.forEach((runner, index) => {
      const originalRunner = allSummaries.find((r) => r.id === runner.id);
      if (originalRunner) {
        originalRunner.gender_place = index + 1;
      }
    });
  });

  // IMPORTANT: Exclude DNS runners from public results
  // DNS runners are hidden from the public live page per the design spec.
  // They still appear correctly in admin/volunteer interfaces.
  // AD-641: anonymous runners are hidden entirely. The live_roster view
  // already leaves them out; this is a second line of defence.
  const publicSummaries = allSummaries.filter((r) => !r.dns && !r.is_anonymous);

  return publicSummaries;
}

/**
 * Fetch detailed runner information with all splits
 * Used for the runner detail slide-over
 */
export async function fetchRunnerDetail(
  runnerId: string,
  raceId: string
): Promise<RunnerDetail | null> {
  // Fetch runner info via the anon-facing live_roster VIEW (runners → live_roster,
  // dictionary §3.12). `runnerId` is a people.id (the view's `id` = people.id and
  // it is what the summary list / checkin_events.runner_id use).
  //
  // AD-230: live_roster is keyed per (race, person), so `id` alone is NOT unique —
  // a runner registered in more than one race has one row per race with the same
  // `id` (e.g. people.id 87 / bib 241 appears in race 2 AND race 11). Filtering by
  // `id` only then matches multiple rows and `.maybeSingle()` returns no row, so
  // the detail page rendered "Runner Not Found" for any multi-race runner. Scope
  // by race_id too (mirrors fetchRunnersSummary's roster query) to select the one
  // intended (race, person) row.
  const { data: rawRunner, error: runnerError } = await supabase
    .from('live_roster')
    .select(ROSTER_COLUMNS)
    .eq('id', runnerId)
    .eq('race_id', raceId)
    .maybeSingle();

  if (runnerError) {
    console.error('Error fetching runner:', runnerError);
    throw new Error('Failed to load runner details. Please try again.');
  }

  if (!rawRunner) {
    return null; // Runner not found - this is a valid case, not an error
  }
  // AD-641: an anonymous runner has no public page (the view already omits
  // them; this guards a stale link or an older view).
  if (rawRunner.is_anonymous) {
    return null;
  }

  const runner = mapRosterRow(rawRunner);

  // Fetch race info to get start time and status. races → race_events;
  // `date` is now `date_start`; status runs through mapRaceStatus (planning→draft,
  // cancelled→completed for status-calc purposes).
  const { data: race, error: raceError } = await supabase
    .from('race_events')
    .select('date_start, start_time, status, time_zone')
    .eq('id', raceId)
    .maybeSingle();

  if (raceError) {
    console.error('Error fetching race:', raceError);
    throw new Error('Failed to load race information. Please try again.');
  }

  if (!race) {
    return null; // Race not found - this is a valid case, not an error
  }

  const mappedStatus = mapRaceStatus(race.status);
  const raceStatus = (mappedStatus === 'cancelled' ? 'completed' : mappedStatus) as
    | 'draft'
    | 'active'
    | 'completed';

  // Fetch runner's actual race_start event (individual start time)
  // Exclude cancelled events - a cancelled race_start should not be used
  const { data: raceStartEvent, error: raceStartError } = await supabase
    .from('checkin_events')
    .select('server_timestamp')
    .eq('race_id', raceId)
    .eq('runner_id', runnerId)
    .eq('event_type', 'race_start')
    .is('cancelled_at', null)
    .maybeSingle();

  if (raceStartError) {
    console.error('Error fetching race start event:', raceStartError);
    // Don't throw - this is not fatal, we can fall back to race start time
  }

  const raceTimeZone = race.time_zone || 'America/Los_Angeles'; // Default for safety

  // Track if runner has an actual race_start event (vs fallback)
  const hasRaceStartEvent = !!raceStartEvent?.server_timestamp;

  // Use runner's individual race_start event if available, otherwise fall back to race start_time
  let raceStartTime: string;
  if (raceStartEvent?.server_timestamp) {
    // Runner has an individual start event - use its server_timestamp (proper UTC)
    raceStartTime = raceStartEvent.server_timestamp;
  } else if (race.start_time) {
    // No race_start event - construct from race start_time (in race's local timezone)
    // This is a fallback for runners who haven't started yet
    // Convert local time to UTC using raceTimeZone before storing
    const localDateTime = `${race.date_start}T${race.start_time}`;
    raceStartTime = localToUTC(localDateTime, raceTimeZone);
  } else {
    // No start time at all - use race date at midnight (local time, convert to UTC)
    const localDateTime = `${race.date_start}T00:00:00`;
    raceStartTime = localToUTC(localDateTime, raceTimeZone);
  }

  // Fetch all aid stations for this race (with fields needed for status calculation)
  // aid_stations → live_stations VIEW (columns pre-aliased to wire names).
  const { data: stationsData, error: stationsError } = await supabase
    .from('live_stations')
    .select('id, race_id, name, mileage, position, cutoff_time, expected_visits, parent_station_id, check_mode')
    .eq('race_id', raceId)
    .order('position', { ascending: true });

  if (stationsError) {
    console.error('Error fetching aid stations:', stationsError);
    throw new Error('Failed to load aid station data. Please refresh the page.');
  }

  const allStations = (stationsData || []) as (AidStation & { expected_visits?: number | null; check_mode?: string | null })[];
  // Display list: without the hidden parent row of a multi-visit station
  // (AD-644). It used to appear in Timing Points ("Cornet · 21.4 mi") and took
  // the NEXT badge from the runner's real next stop. The status calculation
  // below still gets the full list.
  const stations = allStations.filter((s) => !isHiddenParentStation(s));

  // Convert stations to StatusStation format for the status utility
  const statusStations: StatusStation[] = allStations.map(s => ({
    id: s.id,
    name: s.name,
    position: s.position,
    expected_visits: s.expected_visits,
    check_mode: s.check_mode,
  }));

  // Fetch all events for this runner (all types needed for status calculation)
  // Exclude cancelled events - cancelled events should not affect status or finish time calculations
  const { data: events, error: eventsError } = await supabase
    .from('checkin_events')
    .select('station_id, event_type, server_timestamp, device_timestamp, dnf_reason')
    .eq('race_id', raceId)
    .eq('runner_id', runnerId)
    .is('cancelled_at', null)
    .order('device_timestamp', { ascending: true });

  if (eventsError) {
    console.error('Error fetching runner events:', eventsError);
    throw new Error('Failed to load runner check-in history. Please try again.');
  }

  // Type for the event data we're fetching
  type DetailEvent = {
    station_id: string;
    event_type: string;
    server_timestamp: string;
    device_timestamp?: string;
    dnf_reason: string | null;
  };

  const detailEvents = (events || []) as DetailEvent[];

  // Convert events to StatusEvent format for the status utility
  const statusEvents: StatusEvent[] = detailEvents.map(e => ({
    event_type: e.event_type,
    station_id: e.station_id,
    server_timestamp: e.server_timestamp,
    device_timestamp: e.device_timestamp,
  }));

  // Calculate status using the shared utility (matches volunteer app)
  const statusResult = calculateRunnerStatus({
    isDns: runner.dns || false,
    events: statusEvents,
    raceStatus,
    stations: statusStations,
  });

  // DEBUG: Log events to diagnose status issue
  console.log('[RunnerDetail] events passed to calculateRunnerStatus:', {
    bib: runner.bib,
    runner_id: runner.id,
    count: statusEvents.length,
    events: statusEvents,
    hasRaceStart: statusEvents.some(e => e.event_type === 'race_start'),
    statusResult,
    raceStatus,
  });

  // Map status code to CSS class name
  const status = getStatusCssClass(statusResult.statusCode) as RunnerStatus;

  // Get finish event timestamp for finished runners.
  // Events are sorted ascending (oldest first), so the LAST finish event is the most recent.
  // This matches fetchRunnersSummary behavior and is important if a finish was cancelled and the runner finished again.
  const finishEvents = detailEvents.filter((e) => e.event_type === 'finish');
  const finishEvent = finishEvents.length > 0 ? finishEvents[finishEvents.length - 1] : undefined;

  // Build split times array by matching stations to events.
  // The finish line is crossed via a `finish` event (not a check_in), so map that event onto
  // the finish-mode station. Otherwise the finish line never receives a split time and renders
  // as the "next" checkpoint even after the runner has finished (and runnerDistance falls back
  // to the last intermediate aid station instead of the finish line).
  const splitTimes: SplitTime[] = stations.map((station) => {
    const stationEvent =
      station.check_mode === 'finish'
        ? finishEvent
        : detailEvents.find(
            (e) => e.station_id === station.id && e.event_type === 'check_in'
          );

    let elapsedTime: number | null = null;
    if (stationEvent) {
      const eventTime = new Date(scanTime(stationEvent)!).getTime();
      const startTime = new Date(raceStartTime).getTime();
      elapsedTime = eventTime - startTime;
    }

    return {
      station_id: station.id,
      station_name: station.name,
      station_mileage: station.mileage,
      station_position: station.position,
      time: scanTime(stationEvent),
      elapsed_time: elapsedTime,
    };
  });

  // Get latest check-in for last seen info
  const checkInEvents = detailEvents.filter((e) => e.event_type === 'check_in');
  const latestCheckIn = checkInEvents[checkInEvents.length - 1];

  // Get station name for last check-in
  // For DNS runners, hide event info from public view
  const lastStation = runner.dns
    ? null
    : stations.find((s) => s.id === latestCheckIn?.station_id);

  // For overall_place, we'd need to fetch all runners - skip for now in detail view
  // It's already shown in the main list

  return {
    id: runner.id,
    bib: runner.bib,
    first_name: runner.first_name,
    last_name: runner.last_name,
    is_anonymous: runner.is_anonymous,
    early_start: runner.early_start,
    dns: runner.dns || false,
    gender: runner.gender || null,
    status,
    status_label: statusResult.statusLabel,
    status_short_label: statusResult.statusShortLabel,
    is_dropped: statusResult.isDropped,
    last_checkin_station: runner.dns ? null : lastStation?.name || null,
    last_checkin_station_position: runner.dns ? null : lastStation?.position ?? null,
    last_checkin_time: runner.dns ? null : scanTime(latestCheckIn),
    finish_time: runner.dns ? null : scanTime(finishEvent),
    overall_place: null,
    gender_place: null,
    race_start_time: raceStartTime,
    next_checkpoint: statusResult.nextStation || null,
    next_checkpoint_id: statusResult.nextStationId || null,
    phase:
      statusResult.displayStatus === 'at_station'
        ? 'at_station'
        : statusResult.displayStatus === 'enroute'
          ? 'enroute'
          : null,
    has_race_start_event: hasRaceStartEvent,
    race_time_zone: raceTimeZone,
    // For DNS runners, return empty splits (hide event data from public)
    split_times: runner.dns ? [] : splitTimes,
  };
}

/**
 * AD-195: current simulation state for a race (null = not simulating).
 * anon-readable by design — the row is pure clock arithmetic. The EVENT
 * truncation itself is enforced server-side by the checkin_events RLS
 * predicate (server_timestamp <= sim_now(race_id)); this fetch only powers
 * the clock display and the scrub-refetch trigger.
 */
export async function fetchSimulationState(raceId: string | number): Promise<SimulationState | null> {
  const { data, error } = await supabase
    .from('race_simulations')
    .select('anchor_sim_time, anchor_wall_time, speed, playing')
    .eq('race_id', raceId)
    .maybeSingle();
  if (error) {
    console.error('Error fetching simulation state:', error);
    return null;
  }
  return data ? { ...data, speed: Number(data.speed) } as SimulationState : null;
}
